import { useAppDispatch, useAppSelector } from "app/hooks";
import { useForm } from "hooks/useForm";
import CenterContainer from "features/containers/CenterContainer";

import {
  ResetPasswordData,
  selectError,
  selectLoading,
  selectUserEmail,
  verifyResetCode,
} from "./resetPasswordSlice";
import { Alert, Box, Button, Card, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

const EnterNewPassword: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const resetLoading = useAppSelector(selectLoading);
  const resetErrors = useAppSelector(selectError);
  const userEmail = useAppSelector(selectUserEmail);

  // Using form hook for validation
  const {
    handleSubmit,
    handleChange,
    data: resetPasswordData,
    errors,
  } = useForm<ResetPasswordData>({
    validations: {
      newPassword: {
        custom: {
          isValid: (value) => value.length > 0,
          message: "Ange ett nytt lösenord.",
        },
      },
      confirmPassword: {
        custom: {
          isValid: (value) => value === resetPasswordData.newPassword,
          message: "Lösenorden matchar inte.",
        },
      },
      code: {
        custom: {
          isValid: (value) => value.length > 5,
          message: "Ange koden.",
        },
      },
    },
    initialValues: {
      newPassword: "",
      confirmPassword: "",
      email: userEmail || "",
      code: "",
    },
    onSubmit: async () => {
      await dispatch(verifyResetCode(resetPasswordData))
        .unwrap()
        .then(() => {
          navigate("/login");
        });
    },
  });
  return (
    <CenterContainer>
      <Card elevation={8} sx={{ p: 4, borderRadius: "1rem" }}>
        <Typography variant="h1" fontSize="2rem" textAlign="center" mb={2}>
          Ange nytt lösenord
        </Typography>

        <Typography textAlign="center" mb={2}>
          Ett mail har nu skickats till dig med en kod. Ange koden här och välj ett nytt lösenord
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: "grid", gap: 1 }}>
          <TextField
            variant="filled"
            value={resetPasswordData.code}
            onChange={handleChange("code")}
            type="text"
            error={!!errors.code}
            helperText={errors.code}
            fullWidth
            label="Verifieringskod"
          />

          <TextField
            variant="filled"
            value={resetPasswordData.newPassword}
            onChange={handleChange("newPassword")}
            type="password"
            error={!!errors.newPassword}
            helperText={errors.newPassword}
            fullWidth
            label="Nytt lösenord"
            autoComplete="new-password"
          />

          <TextField
            variant="filled"
            value={resetPasswordData.confirmPassword}
            onChange={handleChange("confirmPassword")}
            type="password"
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            fullWidth
            label="Bekräfta nytt lösenord"
            autoComplete="new-password"
          />

          {/* Show server errors if present */}
          {resetErrors && Object.keys(errors).length < 1 && (
            <Alert severity="error">{resetErrors}</Alert>
          )}

          <Button type="submit" variant="contained" loading={resetLoading} size="large">
            Nästa
          </Button>
        </Box>
      </Card>
    </CenterContainer>
  );
};

export default EnterNewPassword;
